import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import PhotoLibraryRoundedIcon from '@mui/icons-material/PhotoLibraryRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import { providersService } from '../../shared/services/providers.service';
import { chatService } from '../../shared/services/chat.service';
import { useAuth } from '../../shared/hooks/useAuth';
import { useNotification } from '../../shared/notifications/NotificationProvider';
import { useApiError } from '../../shared/errors/useApiError';
import { ProviderInlineRating } from './ProviderInlineRating';
import { ProviderRatingsCard } from './sidebar/ProviderRatingsCard';
import { OpeningHoursCard } from './sidebar/OpeningHoursCard';
import { SocialMediaStrip } from '../../shared/components/ui/SocialMediaStrip';
import { imageSrcSet, imageLargeSrc } from '../../shared/utils/images';
import type { PortfolioImage, ProviderServiceItem } from '../../shared/types';

function formatPrice(cents?: number | null, currency?: string | null) {
  if (cents == null || !currency) return null;
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

export function ProviderPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id = '' } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { notify } = useNotification();
  const { translate } = useApiError();
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [contactOpen, setContactOpen] = useState(false);
  const [contactDraft, setContactDraft] = useState('');

  const query = useQuery({
    queryKey: ['provider', id],
    queryFn: () => providersService.get(id),
    enabled: Boolean(id),
  });

  const openInquiry = useMutation({
    mutationFn: (body: string) => chatService.openWithProvider(id, body || undefined),
    onSuccess: (conv) => {
      setContactOpen(false);
      setContactDraft('');
      navigate(`/messages/${conv.id}`);
    },
    onError: (err) => notify.error(translate(err)),
  });

  const startContact = () => {
    if (!user) {
      navigate(`/login?redirect=${encodeURIComponent(`/providers/${id}`)}`);
      return;
    }
    setContactOpen(true);
  };

  const p = query.data;

  const galleryImages = useMemo(() => p?.portfolio ?? [], [p]);
  const heroImage = galleryImages[0];
  const sideThumbs = galleryImages.slice(1, 5);
  const extraCount = Math.max(0, galleryImages.length - 5);

  if (query.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!p) return null;

  const goToBooking = (service: ProviderServiceItem | null = null) => {
    const params = new URLSearchParams();
    if (service) params.set('serviceId', service.id);
    const qs = params.toString();
    navigate(`/providers/${p.id}/book${qs ? `?${qs}` : ''}`);
  };

  const lightboxImage: PortfolioImage | null =
    lightboxIndex != null ? galleryImages[lightboxIndex] ?? null : null;

  return (
    <Box sx={{ pb: { xs: 14, md: 6 } }}>
      <Container maxWidth="lg" sx={{ pt: { xs: 3, md: 5 } }}>
        {/* HEADER */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ sm: 'flex-start' }}
          sx={{ mb: 3 }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h3"
              sx={{ fontSize: { xs: '1.75rem', md: '2.25rem' }, mb: 1 }}
            >
              {p.businessName}
            </Typography>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ color: 'text.secondary' }}
            >
              <Stack direction="row" spacing={0.5} alignItems="center">
                <LocationOnIcon sx={{ fontSize: '1.05rem' }} />
                <Typography
                  component="a"
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    [
                      p.businessName,
                      p.cityName ?? p.city?.name,
                      p.countryCode ?? p.city?.country,
                    ]
                      .filter(Boolean)
                      .join(', '),
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    fontSize: '0.95rem',
                    color: 'text.secondary',
                    textDecoration: 'underline',
                    textUnderlineOffset: 3,
                    '&:hover': { color: 'primary.main' },
                  }}
                >
                  {[p.cityName ?? p.city?.name, p.countryCode ?? p.city?.country]
                    .filter(Boolean)
                    .join(', ')}
                </Typography>
              </Stack>
              <ProviderInlineRating
                score={p.ratingAvg != null ? Number(p.ratingAvg) : null}
                count={p.ratingCount ?? null}
              />
              {/* "Vérifié" badge intentionally hidden — see BookingPage for
                  the rationale. APPROVED means admin-moderated, not
                  identity-verified. */}
            </Stack>
          </Box>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
          >
            <Button
              variant="outlined"
              size="large"
              startIcon={<ChatBubbleOutlineRoundedIcon />}
              onClick={startContact}
              sx={{
                borderColor: '#1A1816',
                color: '#1A1816',
                fontWeight: 700,
                px: 3,
                py: 1.25,
                '&:hover': { borderColor: '#000', bgcolor: 'rgba(0,0,0,0.04)' },
              }}
            >
              {t('chat.contactProvider')}
            </Button>
            <Button
              variant="contained"
              size="large"
              onClick={() => goToBooking()}
              sx={{
                bgcolor: '#1A1816',
                color: 'common.white',
                fontWeight: 700,
                px: 3,
                py: 1.25,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#000', boxShadow: 'none' },
              }}
            >
              {t('provider.bookCta')}
            </Button>
          </Stack>
        </Stack>

        {/* GALLERY — 1 large + up to 4 thumbs (2x2) */}
        {heroImage && (
          <Box sx={{ mb: { xs: 4, md: 6 } }}>
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={sideThumbs.length > 0 ? 7 : 12}>
                <Box
                  onClick={() => setLightboxIndex(0)}
                  sx={{
                    position: 'relative',
                    aspectRatio: '4 / 3',
                    borderRadius: 3,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    '&:hover img': { transform: 'scale(1.04)' },
                  }}
                >
                  <Box
                    component="img"
                    src={imageSrcSet(heroImage.imageUrl).src}
                    srcSet={imageSrcSet(heroImage.imageUrl).srcSet}
                    sizes="(max-width:900px) 100vw, 60vw"
                    alt={heroImage.caption ?? p.businessName}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                      transition: 'transform 500ms ease',
                    }}
                  />
                  {formatPrice(heroImage.priceCents, heroImage.currency) && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        px: 1.5,
                        py: 0.6,
                        borderRadius: 1.75,
                        bgcolor: 'rgba(0,0,0,0.65)',
                        backdropFilter: 'blur(6px)',
                        color: 'common.white',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                      }}
                    >
                      {formatPrice(heroImage.priceCents, heroImage.currency)}
                    </Box>
                  )}
                </Box>
              </Grid>

              {sideThumbs.length > 0 && (
                <Grid item xs={12} md={5}>
                  <Grid container spacing={1.5}>
                    {sideThumbs.map((img, i) => {
                      const isLastSlot =
                        i === sideThumbs.length - 1 && extraCount > 0;
                      const handleClick = () => {
                        if (isLastSlot) {
                          navigate(`/providers/${p.id}/gallery`);
                        } else {
                          setLightboxIndex(i + 1);
                        }
                      };
                      return (
                        <Grid item xs={6} key={img.id}>
                          <Box
                            onClick={handleClick}
                            sx={{
                              position: 'relative',
                              aspectRatio: '1 / 1',
                              borderRadius: 3,
                              overflow: 'hidden',
                              cursor: 'pointer',
                              '&:hover img': { transform: 'scale(1.05)' },
                            }}
                          >
                            <Box
                              component="img"
                              src={imageSrcSet(img.imageUrl).src}
                              srcSet={imageSrcSet(img.imageUrl).srcSet}
                              sizes="(max-width:900px) 48vw, 22vw"
                              alt={img.caption ?? p.businessName}
                              loading="lazy"
                              sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block',
                                transition: 'transform 500ms ease',
                              }}
                            />
                            {isLastSlot && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  inset: 0,
                                  bgcolor: 'rgba(0,0,0,0.55)',
                                  backdropFilter: 'blur(2px)',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: 0.5,
                                  color: 'common.white',
                                }}
                              >
                                <PhotoLibraryRoundedIcon />
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {t('provider.viewAllPhotos', {
                                    count: galleryImages.length,
                                  })}
                                </Typography>
                              </Box>
                            )}
                            {!isLastSlot &&
                              formatPrice(img.priceCents, img.currency) && (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    bottom: 8,
                                    left: 8,
                                    px: 1,
                                    py: 0.4,
                                    borderRadius: 1.25,
                                    bgcolor: 'rgba(0,0,0,0.65)',
                                    backdropFilter: 'blur(6px)',
                                    color: 'common.white',
                                    fontWeight: 700,
                                    fontSize: '0.8125rem',
                                  }}
                                >
                                  {formatPrice(img.priceCents, img.currency)}
                                </Box>
                              )}
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Box>
        )}

        {/* BOOKING INTRO */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ mb: 0.5 }}>
            {t('provider.bookSectionTitle', { business: p.businessName })}
          </Typography>
          <Typography color="text.secondary">
            {t('provider.bookSectionSubtitle')}
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* MAIN COLUMN */}
          <Grid item xs={12} md={8}>
            <Box sx={{ mb: 5 }}>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ letterSpacing: '0.12em', fontWeight: 700 }}
              >
                {t('provider.servicesResults')}
              </Typography>
              <Typography variant="h5" sx={{ mt: 0.5, mb: 2.5 }}>
                {t('provider.services')}
              </Typography>

              {p.services.length === 0 ? (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 4,
                    borderRadius: 3,
                    borderStyle: 'dashed',
                    textAlign: 'center',
                  }}
                >
                  <Typography color="text.secondary">
                    {t('provider.noServices')}
                  </Typography>
                </Paper>
              ) : (
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    overflow: 'hidden',
                  }}
                >
                  {p.services.map((s, i) => {
                    const rawPrice = formatPrice(s.customPriceCents, s.currency);
                    const priceLabel = rawPrice
                      ? (s.priceType === 'STARTING_FROM'
                          ? `${t('provider.fromPrice')} ${rawPrice}`
                          : rawPrice)
                      : null;
                    return (
                      <Box
                        key={s.id}
                        sx={{
                          p: { xs: 2, sm: 2.5 },
                          borderTop: i === 0 ? 'none' : '1px solid',
                          borderColor: 'divider',
                          display: 'flex',
                          alignItems: { xs: 'flex-start', sm: 'center' },
                          gap: { xs: 1.5, sm: 2.5 },
                          flexDirection: { xs: 'column', sm: 'row' },
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 700, mb: 0.25 }}
                          >
                            {s.serviceName}
                          </Typography>
                          {s.description && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 0.5 }}
                            >
                              {s.description}
                            </Typography>
                          )}
                        </Box>

                        <Stack
                          direction="row"
                          spacing={2}
                          alignItems="center"
                          sx={{
                            flexShrink: 0,
                            color: 'text.secondary',
                            minWidth: { sm: 200 },
                            justifyContent: { sm: 'flex-end' },
                          }}
                        >
                          {s.durationMinutes && (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <AccessTimeRoundedIcon sx={{ fontSize: '1rem' }} />
                              <Typography variant="body2">
                                {s.durationMinutes} min
                              </Typography>
                            </Stack>
                          )}
                          {priceLabel && (
                            <Typography
                              variant="subtitle1"
                              sx={{ fontWeight: 700, color: 'text.primary' }}
                            >
                              {priceLabel}
                            </Typography>
                          )}
                        </Stack>

                        <Button
                          variant="contained"
                          onClick={() => goToBooking(s)}
                          sx={{
                            bgcolor: '#1A1816',
                            color: 'common.white',
                            fontWeight: 700,
                            px: 3,
                            py: 1,
                            boxShadow: 'none',
                            alignSelf: { xs: 'stretch', sm: 'auto' },
                            '&:hover': { bgcolor: '#000', boxShadow: 'none' },
                          }}
                        >
                          {t('provider.select')}
                        </Button>
                      </Box>
                    );
                  })}
                </Paper>
              )}
            </Box>
          </Grid>

          {/* SIDEBAR */}
          <Grid item xs={12} md={4}>
            <Box sx={{ position: { md: 'sticky' }, top: { md: 88 } }}>
              {/* Booking card */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  mb: 3,
                  display: { xs: 'none', md: 'block' },
                }}
              >
                <Typography variant="h6" sx={{ mb: 0.5 }}>
                  {t('provider.contact')}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {t('provider.contactHint')}
                </Typography>
                <Stack spacing={1}>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<EventAvailableRoundedIcon />}
                    onClick={() => goToBooking()}
                    fullWidth
                    sx={{
                      py: 1.5,
                      bgcolor: '#1A1816',
                      color: 'common.white',
                      fontWeight: 700,
                      boxShadow: 'none',
                      '&:hover': { bgcolor: '#000', boxShadow: 'none' },
                    }}
                  >
                    {t('provider.bookCta')}
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<ChatBubbleOutlineRoundedIcon />}
                    onClick={startContact}
                    fullWidth
                    sx={{
                      py: 1.5,
                      borderColor: '#1A1816',
                      color: '#1A1816',
                      fontWeight: 700,
                      '&:hover': { borderColor: '#000', bgcolor: 'rgba(0,0,0,0.04)' },
                    }}
                  >
                    {t('chat.contactProvider')}
                  </Button>
                </Stack>
              </Paper>

              {/* Ratings & reviews — placed right after the booking card
                  so social proof is visible without scrolling. The card
                  hides itself when the provider has no reviews yet (fresh
                  shops show no empty state), so it's safe to mount
                  unconditionally. */}
              <Box sx={{ mb: 3 }}>
                <ProviderRatingsCard providerId={p.id} />
              </Box>

              {/* Opening hours */}
              <Box sx={{ mb: 3 }}>
                <OpeningHoursCard providerId={p.id} />
              </Box>

              {/* About */}
              {p.bio && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ letterSpacing: '0.12em', fontWeight: 700 }}
                  >
                    {t('provider.bio')}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      lineHeight: 1.7,
                      mt: 0.5,
                    }}
                  >
                    {p.bio}
                  </Typography>
                </Paper>
              )}

              {/* Social media — discreet icon strip below the bio so it
                  rewards visitors who scroll without competing with the
                  primary booking CTA. Renders nothing when the provider
                  hasn't filled any link in. */}
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-start' }}>
                <SocialMediaStrip social={p.social} />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* MOBILE STICKY CTA */}
      <Box
        sx={{
          display: { xs: 'block', md: 'none' },
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          p: 2,
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          zIndex: 10,
        }}
      >
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="large"
            onClick={startContact}
            sx={{
              py: 1.5,
              borderColor: '#1A1816',
              color: '#1A1816',
              fontWeight: 700,
              flex: '0 0 auto',
              minWidth: 56,
              '&:hover': { borderColor: '#000', bgcolor: 'rgba(0,0,0,0.04)' },
            }}
            aria-label={t('chat.contactProvider')}
          >
            <ChatBubbleOutlineRoundedIcon />
          </Button>
          <Button
            variant="contained"
            size="large"
            onClick={() => goToBooking()}
            sx={{
              flex: 1,
              py: 1.5,
              bgcolor: '#1A1816',
              color: 'common.white',
              fontWeight: 700,
              boxShadow: 'none',
              '&:hover': { bgcolor: '#000', boxShadow: 'none' },
            }}
          >
            {t('provider.bookCta')}
          </Button>
        </Stack>
      </Box>

      {/* CONTACT PROVIDER */}
      <Dialog
        open={contactOpen}
        onClose={() => !openInquiry.isPending && setContactOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pb: 0.5 }}>
          {t('chat.contactDialogTitle', { business: p.businessName })}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('chat.contactDialogHint')}
          </Typography>
          <TextField
            autoFocus
            multiline
            minRows={4}
            maxRows={10}
            fullWidth
            value={contactDraft}
            onChange={(e) => setContactDraft(e.target.value)}
            placeholder={t('chat.contactDialogPlaceholder')}
            inputProps={{ maxLength: 4000 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setContactOpen(false)}
            disabled={openInquiry.isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => openInquiry.mutate(contactDraft.trim())}
            disabled={!contactDraft.trim() || openInquiry.isPending}
            sx={{
              bgcolor: '#1A1816',
              '&:hover': { bgcolor: '#000' },
            }}
          >
            {openInquiry.isPending ? t('common.loading') : t('chat.send')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* LIGHTBOX */}
      <Dialog
        open={Boolean(lightboxImage)}
        onClose={() => setLightboxIndex(null)}
        maxWidth="lg"
        PaperProps={{
          sx: { bgcolor: 'transparent', boxShadow: 'none', m: { xs: 1, md: 3 } },
        }}
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          <IconButton
            onClick={() => setLightboxIndex(null)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(0,0,0,0.55)',
              color: 'common.white',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
              zIndex: 1,
            }}
          >
            <CloseRoundedIcon />
          </IconButton>
          {lightboxImage && (
            <Box sx={{ position: 'relative' }}>
              <Box
                component="img"
                src={imageLargeSrc(lightboxImage.imageUrl)}
                alt={lightboxImage.caption ?? p.businessName}
                sx={{
                  width: '100%',
                  maxHeight: '85vh',
                  objectFit: 'contain',
                  display: 'block',
                  borderRadius: 2,
                }}
              />
              {(lightboxImage.caption ||
                formatPrice(lightboxImage.priceCents, lightboxImage.currency)) && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    p: { xs: 2, sm: 3 },
                    background:
                      'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.85) 100%)',
                    color: 'common.white',
                    borderBottomLeftRadius: 8,
                    borderBottomRightRadius: 8,
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'space-between',
                    gap: 2,
                  }}
                >
                  <Box>
                    {lightboxImage.caption && (
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {lightboxImage.caption}
                      </Typography>
                    )}
                  </Box>
                  {formatPrice(lightboxImage.priceCents, lightboxImage.currency) && (
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: 800, color: 'common.white' }}
                    >
                      {formatPrice(lightboxImage.priceCents, lightboxImage.currency)}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
